/**
 * Removes what the service worker cached at runtime: the catalogue responses
 * (`/api/categories`, `/api/moods`, `/api/fantasy-catalog`, `/api/sex-toys`,
 * several behind login, `/moods` depends on the account's settings) and the
 * illustrations that were viewed (their file names say what was looked at).
 * Neither should outlive a session on a shared device. Never throws.
 */
import { clearLastUser } from '../store/lastUserMarker';
import { API_CATALOG_CACHE_NAME, ASSET_CACHE_NAME } from './runtimeCaching';

export const RUNTIME_CACHE_NAMES = [
  API_CATALOG_CACHE_NAME,
  ASSET_CACHE_NAME,
] as const;

export async function clearRuntimeCaches(): Promise<void> {
  try {
    if (typeof caches === 'undefined') {
      return;
    }
    await Promise.all(RUNTIME_CACHE_NAMES.map(name => caches.delete(name)));
  } catch {
    // Cache Storage blocked or unavailable: nothing was stored either.
  }
}

interface AuthStoreLike {
  getState: () => unknown;
  subscribe: (listener: () => void) => () => void;
}

function isAuthenticated(state: unknown): boolean {
  const auth = (state as { auth?: { isAuthenticated?: boolean } } | undefined)
    ?.auth;
  return auth?.isAuthenticated === true;
}

/**
 * Clears the runtime caches and the offline "last user" note every time the
 * session goes from signed in to out (logout, 401, account deletion).
 */
export function clearRuntimeCachesOnSignOut(store: AuthStoreLike): () => void {
  let wasAuthenticated = isAuthenticated(store.getState());
  return store.subscribe(() => {
    const now = isAuthenticated(store.getState());
    if (wasAuthenticated && !now) {
      clearLastUser();
      void clearRuntimeCaches();
    }
    wasAuthenticated = now;
  });
}
